import { FormEvent, useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import InputField from '../../../components/Forms/Input/InputField';
import IconPickerField from '../../../components/Forms/Input/IconPickerField';
import ToggleSwitch from '../../../components/Forms/Input/ToggleSwitch';
import FormModal from '../../../components/Modals/FormModal';
import RestaurantFormHelp from '../shared/RestaurantFormHelp';
import { RestaurantRawMaterialCategoryForm } from '../../../types/restaurant/RestaurantRawMaterialCategoryTypes';

interface Props {
  isOpen: boolean;
  form: RestaurantRawMaterialCategoryForm;
  saving: boolean;
  onClose: () => void;
  onChange: <K extends keyof RestaurantRawMaterialCategoryForm>(
    field: K,
    value: RestaurantRawMaterialCategoryForm[K]
  ) => void;
  onSave: () => void;
}

const RawMaterialCategoryFormModal = ({
  isOpen,
  form,
  saving,
  onClose,
  onChange,
  onSave,
}: Props) => {
  const errors = useMemo(() => {
    const x: Record<string, string> = {};
    if (!form.name.trim()) x.name = 'El nombre es obligatorio.';
    else if (form.name.length > 100) x.name = 'Máximo 100 caracteres.';
    return x;
  }, [form]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!Object.keys(errors).length) onSave();
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={saving ? undefined : onClose}
      title={
        form.id
          ? 'Editar categoría de materia prima'
          : 'Nueva categoría de materia prima'
      }
      maxWidth="max-w-2xl"
      headerExtra={<RestaurantFormHelp topic="rawMaterialCategory" />}
    >
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-1 gap-x-5 md:grid-cols-2">
          <InputField
            label="Nombre"
            name="name"
            type="text"
            iconKey="category"
            value={form.name}
            required
            error={errors.name}
            placeholder="Ej. Carnes"
            onChange={(e) => onChange('name', e.target.value)}
          />

          <IconPickerField
            label="Ícono"
            name="icon"
            iconKey="box"
            value={form.icon}
            onChange={(value) => onChange('icon', value)}
          />

          <InputField
            label="Orden"
            name="displayOrder"
            type="number"
            iconKey="list"
            value={form.displayOrder}
            min={0}
            onChange={(e) =>
              onChange('displayOrder', Math.max(0, Number(e.target.value)))
            }
          />

          <div className="mb-5">
            <label className="mb-2.5 block text-sm font-medium text-black dark:text-white">
              Color
            </label>
            <div className="flex items-center gap-3 rounded-xl border border-stroke bg-gray-1 px-3 py-2.5 dark:border-strokedark dark:bg-meta-4">
              <input
                type="color"
                value={form.color || '#10B981'}
                onChange={(e) => onChange('color', e.target.value)}
                className="h-9 w-12 cursor-pointer border-0 bg-transparent"
              />
              <input
                value={form.color}
                onChange={(e) => onChange('color', e.target.value)}
                className="min-w-0 flex-1 bg-transparent text-sm text-black outline-none dark:text-white"
              />
            </div>
          </div>

          {form.id && (
            <>
              <InputField
                label="Código"
                name="code"
                type="text"
                iconKey="code"
                value={form.code ?? ''}
                disabled
                onChange={() => undefined}
              />

              <div className="mb-5 flex items-center rounded-xl border border-stroke px-4 py-3 dark:border-strokedark">
                <ToggleSwitch
                  label="Categoría activa"
                  name="rawCategoryActive"
                  checked={form.active ?? true}
                  onChange={() => onChange('active', !(form.active ?? true))}
                />
              </div>
            </>
          )}
        </div>

        <div>
          <label className="mb-2.5 block text-sm font-medium text-black dark:text-white">
            Descripción
          </label>
          <textarea
            rows={3}
            maxLength={300}
            value={form.description}
            onChange={(e) => onChange('description', e.target.value)}
            className="w-full rounded-xl border border-stroke bg-gray-1 px-5 py-3 text-sm outline-none focus:border-primary dark:border-strokedark dark:bg-meta-4 dark:text-white"
          />
          <p className="mt-1 text-right text-xs text-bodydark2">
            {form.description.length}/300
          </p>
        </div>

        <div className="flex justify-end gap-3 border-t border-stroke pt-5 dark:border-strokedark">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-stroke px-5 py-2.5 dark:border-strokedark"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={saving || !!Object.keys(errors).length}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2.5 font-medium text-white disabled:opacity-50"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Guardar categoría
          </button>
        </div>
      </form>
    </FormModal>
  );
};

export default RawMaterialCategoryFormModal;
